import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Alert,
  IconButton,
  Typography,
} from '@mui/material';
import { Description, Close } from '@mui/icons-material';
import { RefundView } from '../../../../../services/RefundService/types/refundTypes';
import { DocumentView } from '../../../../../services/RefundService/types/documentTypes';
import { useDocumentActions } from './hooks';
import {
  DocumentInfoSection,
  DocumentMetadataSection,
  DocumentActionsSection,
} from './components';

export interface ViewDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  document: DocumentView | null;
  refund: RefundView;
  onDocumentDeleted?: (documentId: string) => void;
}

export const ViewDocumentDialog: React.FC<ViewDocumentDialogProps> = ({
  open,
  onClose,
  document: documentInfo,
  refund,
  onDocumentDeleted,
}) => {
  const {
    canDelete,
    isDownloading,
    isViewing,
    isDeleting,
    handleDownload,
    handleView,
    handleDelete,
  } = useDocumentActions({
    document: documentInfo,
    refund,
    onClose,
    onDocumentDeleted,
  });

  if (!documentInfo) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Description sx={{ color: '#3b82f6', fontSize: 28 }} />
          <Typography variant="h6">Document Details</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Stack spacing={3}>
          <DocumentInfoSection document={documentInfo} />
          <DocumentMetadataSection document={documentInfo} />
          <DocumentActionsSection
            canDelete={canDelete}
            isDownloading={isDownloading}
            isViewing={isViewing}
            isDeleting={isDeleting}
            onView={handleView}
            onDownload={handleDownload}
            onDelete={handleDelete}
          />

          {!canDelete && (
            <Alert
              severity="info"
              sx={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
              }}
            >
              <Typography variant="body2">
                You can only delete documents that you have uploaded.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: '#d1d5db',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDocumentDialog;
